/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * TemplatePreviewModal — renders a saved section template inside the preview
 * iframe so the admin can see what it looks like before adding it to the canvas.
 * Reuses the same postMessage protocol as LivePreview.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';

const PREVIEW_URL = process.env.REACT_APP_PUBLIC_CLIENT_URL
  ? `${process.env.REACT_APP_PUBLIC_CLIENT_URL}/p/preview`
  : null;

const WIDTHS = { desktop: '100%', mobile: '375px' };

function templateToSection(tpl) {
  const cfg = tpl.default_config || {};
  return {
    id: null,
    _clientId: `preview-${tpl.id}`,
    section_type: tpl.section_type,
    order: 0,
    is_visible: true,
    responsive_config: { hide_on: [] },
    layout_config: cfg.layout_config || {},
    data_binding: cfg.data_binding || null,
    section_config: cfg.section_config || {},
  };
}

export default function TemplatePreviewModal({ template, onClose }) {
  const { theme } = useTheme();
  const iframeRef = useRef(null);
  const [mode, setMode] = useState('desktop');
  const [ready, setReady] = useState(false);

  const sections = [templateToSection(template)];

  const sendSchema = () => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'ARCEE_PAGE_SCHEMA', schema: { sections }, theme },
      '*'
    );
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ARCEE_PREVIEW_READY') {
        setReady(true);
        sendSchema();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ready) sendSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, mode]);

  if (!PREVIEW_URL) {
    return (
      <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Template Preview</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body text-center py-5 text-muted">
              <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: 32 }} />
              <p className="mt-3">
                <code>REACT_APP_PUBLIC_CLIENT_URL</code> is not configured.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: mode === 'mobile' ? 440 : 960, width: '95vw' }}
      >
        <div className="modal-content" style={{ overflow: 'hidden' }}>

          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              background: '#1e293b',
            }}
          >
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0 }}>
              <i className="bi bi-eye me-2" />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {template.name}
              </span>
              <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', textTransform: 'capitalize', fontWeight: 400 }}>
                {template.section_type?.replace(/_/g, ' ')}
              </span>
            </div>

            {/* Responsive toggle */}
            <div className="btn-group" role="group">
              {['desktop', 'mobile'].map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`btn btn-sm ${mode === m ? 'btn-light' : 'btn-outline-light'}`}
                  style={{ fontSize: 12 }}
                  title={m === 'desktop' ? 'Desktop view' : 'Mobile view'}
                >
                  <i className={`bi ${m === 'desktop' ? 'bi-display' : 'bi-phone'}`} />
                </button>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={onClose}
              style={{ fontSize: 12 }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* iframe */}
          <div
            style={{
              background: '#f1f5f9',
              display: 'flex',
              justifyContent: 'center',
              padding: mode === 'mobile' ? '16px' : 0,
              minHeight: 360,
            }}
          >
            <div
              style={{
                width: WIDTHS[mode],
                maxWidth: '100%',
                background: '#fff',
                boxShadow: mode === 'mobile' ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
                transition: 'width 0.25s ease',
                position: 'relative',
              }}
            >
              {!ready && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    fontSize: 13,
                    background: '#fff',
                  }}
                >
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading preview…
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={PREVIEW_URL}
                title="Template Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 360,
                  border: 'none',
                  display: 'block',
                  opacity: ready ? 1 : 0,
                  transition: 'opacity 0.2s',
                }}
              />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
